import { memo, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, keys } from "@shared/api";
import {
  useEmojiSet,
  rememberEmoji,
  recentEmoji,
  searchCustom,
  searchEmoji,
  setSkinTone,
  skinTone,
  toEmoji,
  withTone,
  TONE_SAMPLES,
  type CustomEmoji,
  type Emoji,
  type EmojiRow,
} from "@shared/lib/emoji";
import { FileImage } from "@shared/ui/FileImage";
import {
  IconBike,
  IconChevronDown,
  IconEmoji,
  IconGame,
  IconHeart,
  IconLeaf,
  IconLightbulb,
  IconLock,
  IconRecent,
  IconSearch,
  IconSoup,
  IconStar,
} from "@shared/ui/icons";
import { Spinner } from "@shared/ui/spinner";

/**
 * Панель выбора знака.
 *
 * Она одна на всё приложение: и поле ввода, и ряд реакций открывают этот же
 * объект, а не два похожих. Иначе недавние разойдутся, и человек перестанет
 * понимать, где он только что видел нужный знак.
 *
 * Раскладка — как в Discord: сверху поиск и выбор тона кожи (выпадающим
 * списком), слева колонка разделов — избранное, часто используемые, серверы,
 * а через разделитель — разделы обычных эмодзи; справа разделы со стрелкой
 * (сворачиваются), в подвале — знак под курсором и чей он.
 *
 * Знаки чужих серверов видны, но поставить их здесь нельзя: в тексте знак
 * записан именем и ищется на этом сервере (как в Discord без подписки).
 *
 * Набор грузится по требованию — при первом открытии. До него панель стоит на
 * месте с той же высотой: подпрыгивающее окно хуже пустого.
 *
 * Это только нутро: закрытие по Esc и щелчку мимо, возврат фокуса и место на
 * экране — за `Popover` у того, кто её открывает.
 */

/** Восемь в ряд — как в макете: палец на планшете попадает, мышь не мажет. */
const COLUMNS = 8;

/** Значки разделов в колонке — Lucide, как глифы разделов у Discord. */
const SECTION_ICONS: Record<string, ReactNode> = {
  people: <IconEmoji size={18} />,
  nature: <IconLeaf size={18} />,
  food: <IconSoup size={18} />,
  activity: <IconGame size={18} />,
  places: <IconBike size={18} />,
  objects: <IconLightbulb size={18} />,
  symbols: <IconHeart size={18} />,
};

/** Сервер в колонке: имя и значок рисует тот, кто открывает панель (`ServerTile`). */
export interface PickerServer {
  id: string;
  name: string;
  tile: (size: number) => ReactNode;
}

/** Что показывает подвал: знак под курсором. */
type Hovered =
  | { kind: "unicode"; emoji: Emoji }
  | { kind: "custom"; emoji: CustomEmoji; locked: boolean }
  | null;

interface Part {
  key: string;
  title: string;
  note?: string;
  mark: ReactNode;
  rail: ReactNode;
  /** Начинает вторую часть колонки — обычные эмодзи. */
  divided?: boolean;
  cells: () => ReactNode;
}

export function EmojiPicker({
  custom,
  onPick,
  embedded = false,
  here = null,
  others = [],
}: {
  /** Кастомные знаки этого сервера. В личных переписках их нет. */
  custom: CustomEmoji[];
  /**
   * Выбранный знак. Обычный приходит символом, кастомный — именем в
   * двоеточиях: в тексте сообщения он так и хранится.
   */
  onPick: (value: string) => void;
  /**
   * Вкладка общей панели у поля ввода (`ExpressionPicker`): своей подложки и
   * размера нет, их даёт панель вокруг.
   */
  embedded?: boolean;
  /** Этот сервер — заголовок раздела его знаков и значок в колонке. */
  here?: PickerServer | null;
  /** Остальные серверы со своими знаками: видны, но под замком. */
  others?: (PickerServer & { emoji: CustomEmoji[] })[];
}) {
  const set = useEmojiSet();
  const [query, setQuery] = useState("");
  const [tone, setTone] = useState(skinTone);
  const [toneOpen, setToneOpen] = useState(false);
  const [hovered, setHovered] = useState<Hovered>(null);
  const [folded, setFolded] = useState<Set<string>>(() => new Set());
  const [active, setActive] = useState<string | null>(null);

  const box = useRef<HTMLDivElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  /** Заголовки разделов — по ним прыгает колонка и следит подсветка в ней. */
  const heads = useRef(new Map<string, HTMLElement>());

  /*
   * Недавние снимаются один раз, при открытии.
   *
   * Не на каждую отрисовку: выбор знака кладёт его в недавние, и живой
   * список переставил бы сетку прямо под курсором — там, куда человек уже
   * целится.
   */
  const recent = useMemo(recentEmoji, []);

  /*
   * Избранное — как в Discord: правый щелчок по знаку кладёт его сюда или
   * убирает. Список общий для устройств и правится сразу, без ожидания ответа.
   */
  const queryClient = useQueryClient();
  const { data: favorites = [], isSuccess: favoritesKnown } = useQuery({
    queryKey: keys.favoriteEmoji(),
    queryFn: () => api.emoji.favorites().then((value) => value.items),
  });
  /** Номер последней правки: ответ на устаревшую правку список не трогает. */
  const favoritesEdit = useRef(0);
  function toggleFavorite(value: string) {
    // Пока список не пришёл, правка его бы затёрла — список уходит на сервер целиком.
    if (!favoritesKnown) return;
    void queryClient.cancelQueries({ queryKey: keys.favoriteEmoji() });
    const current = queryClient.getQueryData<string[]>(keys.favoriteEmoji()) ?? favorites;
    const next = current.includes(value) ? current.filter((item) => item !== value) : [value, ...current];
    queryClient.setQueryData(keys.favoriteEmoji(), next);
    const edit = ++favoritesEdit.current;
    void api.emoji
      .saveFavorites(next)
      .then((saved) => {
        if (edit === favoritesEdit.current) queryClient.setQueryData(keys.favoriteEmoji(), saved.items);
      })
      .catch(() => void queryClient.invalidateQueries({ queryKey: keys.favoriteEmoji() }));
  }

  // Поиск — первое, чего ждут от панели: набирают чаще, чем листают.
  useEffect(() => {
    box.current?.querySelector("input")?.focus();
  }, [set]);

  const searching = query.trim().length > 0;
  const found = useMemo(() => (set && searching ? searchEmoji(set, query) : []), [set, query, searching]);
  const foundCustom = useMemo(() => (searching ? searchCustom(custom, query) : []), [custom, query, searching]);
  const foundLocked = useMemo(
    () => (searching ? searchCustom(others.flatMap((server) => server.emoji), query) : []),
    [others, query, searching],
  );

  /** Знаки раздела в порядке набора. */
  const bySection = useMemo(() => {
    if (!set) return [];
    return set.sections.map((_, index) => set.emoji.filter((row) => row[3] === index));
  }, [set]);

  /**
   * Выбор запоминается здесь, а не у каждого, кто открывает панель.
   *
   * Иначе недавние пополнялись бы только с того входа, где про это не забыли,
   * — а панель одна ровно затем, чтобы память была общей.
   */
  function pick(value: string) {
    rememberEmoji(value);
    onPick(value);
  }

  function pickUnicode(row: EmojiRow) {
    pick(withTone(row, tone));
  }

  function chooseTone(next: number) {
    setTone(next);
    setSkinTone(next);
    setToneOpen(false);
  }

  const hereName = here?.name ?? custom[0]?.server_name ?? "Этот сервер";
  const hereMark = here ? here.tile(16) : <IconEmoji size={14} />;

  const parts: Part[] = !set
    ? []
    : searching
      ? [
          {
            key: "found",
            title: "Найдено",
            note: `по «${query.trim()}»`,
            mark: <IconSearch size={14} />,
            rail: null,
            cells: () =>
              found.map((row) => <UnicodeCell key={row[0]} row={row} tone={tone} onPick={pickUnicode} onHover={setHovered} />),
          },
          ...(foundCustom.length > 0
            ? [
                {
                  key: "found-here",
                  title: hereName,
                  mark: hereMark,
                  rail: null,
                  cells: () =>
                    foundCustom.map((item) => <CustomCell key={item.id} emoji={item} onPick={pick} onHover={setHovered} />),
                },
              ]
            : []),
          ...(foundLocked.length > 0
            ? [
                {
                  key: "found-locked",
                  title: "Других серверов",
                  note: "только на своём сервере",
                  mark: <IconLock size={13} />,
                  rail: null,
                  cells: () =>
                    foundLocked.map((item) => <CustomCell key={item.id} emoji={item} locked onPick={pick} onHover={setHovered} />),
                },
              ]
            : []),
        ]
      : [
          ...(favorites.length > 0
            ? [
                {
                  key: "favorites",
                  title: "Избранное",
                  note: "правый щелчок — убрать",
                  mark: <IconStar size={14} />,
                  rail: <IconStar size={18} />,
                  cells: () =>
                    favorites.map((char) => (
                      <RecentCell key={char} char={char} custom={custom} onPick={pick} onHover={setHovered} />
                    )),
                },
              ]
            : []),
          ...(recent.length > 0
            ? [
                {
                  key: "recent",
                  title: "Часто используемые",
                  note: favorites.length === 0 ? "правый щелчок по знаку — в избранное" : undefined,
                  mark: <IconRecent size={14} />,
                  rail: <IconRecent size={18} />,
                  cells: () =>
                    recent.map((char) => (
                      <RecentCell key={char} char={char} custom={custom} onPick={pick} onHover={setHovered} />
                    )),
                },
              ]
            : []),
          ...(custom.length > 0
            ? [
                {
                  key: "here",
                  title: hereName,
                  mark: hereMark,
                  rail: here ? here.tile(32) : <IconEmoji size={18} />,
                  cells: () => custom.map((item) => <CustomCell key={item.id} emoji={item} onPick={pick} onHover={setHovered} />),
                },
              ]
            : []),
          ...others.map((server) => ({
            key: `server:${server.id}`,
            title: server.name,
            mark: server.tile(16),
            rail: (
              <span className="pick-rail-locked">
                {server.tile(32)}
                <span className="pick-lock">
                  <IconLock size={9} />
                </span>
              </span>
            ),
            cells: () =>
              server.emoji.map((item) => <CustomCell key={item.id} emoji={item} locked onPick={pick} onHover={setHovered} />),
          })),
          ...set.sections.map((section, index) => ({
            key: `s${index}`,
            title: section.label,
            mark: <span className="pick-mark-glyph">{SECTION_ICONS[section.key] ?? section.icon}</span>,
            rail: SECTION_ICONS[section.key] ?? section.icon,
            divided: index === 0,
            cells: () =>
              bySection[index].map((row) => (
                <UnicodeCell key={row[0]} row={row} tone={tone} onPick={pickUnicode} onHover={setHovered} />
              )),
          })),
        ];

  function jump(key: string) {
    setQuery("");
    setFolded((value) => {
      if (!value.has(key)) return value;
      const next = new Set(value);
      next.delete(key);
      return next;
    });
    setActive(key);
    // Поиск сброшен — разделы появятся на следующей отрисовке.
    requestAnimationFrame(() => heads.current.get(key)?.scrollIntoView({ block: "start" }));
  }

  function fold(key: string) {
    setFolded((value) => {
      const next = new Set(value);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  // Подсветка в колонке — у раздела, чей заголовок последним ушёл под верх сетки.
  function track() {
    const list = scroller.current;
    if (!list || searching) return;
    let current: string | null = null;
    for (const part of parts) {
      const head = heads.current.get(part.key);
      if (head && head.offsetTop - list.offsetTop <= list.scrollTop + 4) current = part.key;
    }
    setActive(current ?? parts[0]?.key ?? null);
  }

  const railParts = parts.filter((part) => part.rail);
  const lit = searching ? null : (active ?? parts[0]?.key ?? null);
  const placeholder =
    hovered?.kind === "unicode" ? `:${hovered.emoji.code}:` : hovered ? `:${hovered.emoji.name}:` : "Поиск по названию";

  return (
    /*
     * Роль и подпись — на слое `Popover`: он и есть окно, а это его нутро.
     * Материал «Почты» (`surface-pop`).
     */
    <div
      className={embedded ? "emoji-panel embedded" : "emoji-panel surface-pop"}
      ref={box}
      onPointerDown={(event) => {
        if (toneOpen && !(event.target as HTMLElement).closest(".emoji-tone-box")) setToneOpen(false);
      }}
    >
      <div className="emoji-top">
        <div className="emoji-search">
          <span className="emoji-search-glass" aria-hidden>
            <IconSearch size={13} />
          </span>
          <input
            type="text"
            value={query}
            placeholder={placeholder}
            aria-label="Поиск знака по названию"
            onChange={(event) => setQuery(event.target.value)}
          />
          {searching && <span className="emoji-search-count">{found.length + foundCustom.length}</span>}
        </div>

        {/*
          Тон кожи — выпадающим списком у поиска, как в Discord. Шесть образцов,
          а не пять: помимо тонов нужен и исходный вид знака, иначе выбранный
          однажды тон снять было бы нечем.
        */}
        <div className="emoji-tone-box">
          <button
            type="button"
            className={`emoji-tone-pick ${toneOpen ? "on" : ""}`}
            title="Тон кожи"
            aria-label="Тон кожи"
            aria-expanded={toneOpen}
            onClick={() => setToneOpen((value) => !value)}
          >
            {TONE_SAMPLES[tone]}
          </button>
          {toneOpen && (
            <div className="emoji-tone-menu" role="listbox" aria-label="Тон кожи">
              {TONE_SAMPLES.map((sample, index) => (
                <button
                  key={sample}
                  type="button"
                  role="option"
                  aria-selected={index === tone}
                  className={`emoji-tone ${index === tone ? "on" : ""}`}
                  title={index === 0 ? "Как в наборе" : `Тон ${index}`}
                  onClick={() => chooseTone(index)}
                >
                  {sample}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="emoji-body">
        {railParts.length > 0 && (
          <nav className="pick-rail" aria-label="Разделы знаков">
            {railParts.map((part) => (
              <span key={part.key} className="pick-rail-slot">
                {part.divided && <span className="pick-rail-sep" aria-hidden />}
                <button
                  type="button"
                  className={`pick-rail-item ${lit === part.key ? "on" : ""}`}
                  title={part.title}
                  onClick={() => jump(part.key)}
                >
                  {part.rail}
                </button>
              </span>
            ))}
          </nav>
        )}

        <div
          className="emoji-grid"
          ref={scroller}
          onScroll={track}
          onContextMenu={(event) => {
            const cell = (event.target as HTMLElement).closest<HTMLElement>("[data-emoji]");
            if (!cell?.dataset.emoji || cell.dataset.locked) return;
            event.preventDefault();
            toggleFavorite(cell.dataset.emoji);
          }}
        >
          {!set && (
            <div className="emoji-waiting">
              <Spinner />
            </div>
          )}

          {parts.map((part) => (
            <div key={part.key} className="emoji-block">
              <button
                type="button"
                className="pick-head"
                ref={(node) => {
                  if (node) heads.current.set(part.key, node);
                  else heads.current.delete(part.key);
                }}
                aria-expanded={!folded.has(part.key)}
                onClick={() => fold(part.key)}
              >
                <span className="pick-mark">{part.mark}</span>
                <span className="ellipsis">{part.title}</span>
                <IconChevronDown size={14} className="pick-chevron" />
                {part.note && <span className="emoji-block-note">{part.note}</span>}
              </button>
              {!folded.has(part.key) && (
                <div className="emoji-cells" style={{ gridTemplateColumns: `repeat(${COLUMNS}, 1fr)` }}>
                  {part.cells()}
                </div>
              )}
            </div>
          ))}

          {set && searching && found.length === 0 && foundCustom.length === 0 && foundLocked.length === 0 && (
            <div className="emoji-nothing">Ничего не нашлось</div>
          )}
        </div>
      </div>

      <Footer hovered={hovered} here={here} others={others} />
    </div>
  );
}

/**
 * Клетка обычного знака.
 *
 * Отдельным memo-компонентом не для красоты: знаков почти две тысячи, а
 * подвал перерисовывается на каждое движение мыши. Без этого одна проводка
 * курсором по сетке пересобирала бы её целиком десятки раз в секунду.
 */
const UnicodeCell = memo(function UnicodeCell({
  row,
  tone,
  onPick,
  onHover,
}: {
  row: EmojiRow;
  tone: number;
  onPick: (row: EmojiRow) => void;
  onHover: (value: Hovered) => void;
}) {
  const char = withTone(row, tone);

  return (
    <button
      type="button"
      className="emoji-cell"
      data-emoji={char}
      title={row[1]}
      onClick={() => onPick(row)}
      onMouseEnter={() => onHover({ kind: "unicode", emoji: toEmoji(row, tone) })}
    >
      {char}
    </button>
  );
});

/** Клетка кастомного знака. Знак чужого сервера — под замком: виден, но не ставится. */
const CustomCell = memo(function CustomCell({
  emoji,
  locked = false,
  onPick,
  onHover,
}: {
  emoji: CustomEmoji;
  locked?: boolean;
  onPick: (value: string) => void;
  onHover: (value: Hovered) => void;
}) {
  return (
    <button
      type="button"
      className={`emoji-cell ${locked ? "locked" : ""}`}
      data-emoji={`:${emoji.name}:`}
      data-locked={locked || undefined}
      title={locked ? `:${emoji.name}: — только на сервере «${emoji.server_name}»` : `:${emoji.name}:`}
      aria-disabled={locked || undefined}
      onClick={() => !locked && onPick(`:${emoji.name}:`)}
      onMouseEnter={() => onHover({ kind: "custom", emoji, locked })}
    >
      <FileImage id={emoji.file_id} alt={`:${emoji.name}:`} className="emoji-image" />
    </button>
  );
});

/**
 * Клетка недавнего.
 *
 * Недавние хранятся строками, и среди них попадаются имена кастомных знаков.
 * Знак, которого на сервере уже нет — удалили или он был с другого, — из
 * ряда просто выпадает: рисовать пустую рамку в списке недавнего незачем.
 */
function RecentCell({
  char,
  custom,
  onPick,
  onHover,
}: {
  char: string;
  custom: CustomEmoji[];
  onPick: (value: string) => void;
  onHover: (value: Hovered) => void;
}) {
  const name = /^:([^:]+):$/.exec(char)?.[1];

  if (name) {
    const known = custom.find((item) => item.name === name);
    if (!known) return null;
    return <CustomCell emoji={known} onPick={onPick} onHover={onHover} />;
  }

  return (
    <button
      type="button"
      className="emoji-cell"
      data-emoji={char}
      title={char}
      onClick={() => onPick(char)}
      onMouseEnter={() => onHover(null)}
    >
      {char}
    </button>
  );
}

/**
 * Подвал: знак под курсором, его код и чей он — как в Discord: «от Сервер» и
 * значок сервера справа. У знака чужого сервера — что поставить его можно
 * только там.
 */
function Footer({
  hovered,
  here,
  others,
}: {
  hovered: Hovered;
  here: PickerServer | null;
  others: PickerServer[];
}) {
  const owner =
    hovered?.kind === "custom"
      ? (hovered.locked ? others.find((server) => server.id === hovered.emoji.server_id) : here) ?? null
      : null;

  return (
    <div className="emoji-foot">
      <span className="emoji-foot-mark">
        {hovered?.kind === "custom" ? (
          <FileImage id={hovered.emoji.file_id} alt={`:${hovered.emoji.name}:`} className="emoji-image" />
        ) : (
          hovered?.emoji.char
        )}
      </span>

      <span className="emoji-foot-text">
        <span className="emoji-foot-name ellipsis">
          {hovered?.kind === "custom" ? `:${hovered.emoji.name}:` : hovered && `:${hovered.emoji.code}:`}
        </span>
        <span className="emoji-foot-code ellipsis">
          {hovered?.kind === "custom"
            ? hovered.locked
              ? `только на сервере «${hovered.emoji.server_name}»`
              : `от ${hovered.emoji.server_name}`
            : hovered?.emoji.label}
        </span>
      </span>

      {owner && <span className="emoji-foot-server">{owner.tile(28)}</span>}
    </div>
  );
}
